/**
 * Google Places lookups for the origin/destination boxes, the same two calls
 * the platform's other address boxes make (`AutocompleteService`, then
 * `PlacesService.getDetails` for the coordinates). Both are unavailable until
 * the Maps script has loaded; callers fall back to saved locations and codes.
 */

export interface PlacePrediction {
  id: string;
  label: string;
  /** Google files this place as an airport ("Los Angeles International Airport (LAX)"). */
  airport?: boolean;
}

export interface PlaceCoordinates {
  lat: number;
  lng: number;
}

/**
 * Google bills the autocomplete requests typed before a pick as one session
 * when they share a session token and the pick's place-details call ends it.
 * Each place box keeps its own session (Origin and Destination are typed
 * independently), created lazily and renewed once a pick has ended it.
 */
export interface PlacesSession {
  /** The token to send; undefined where the Maps script has no tokens. */
  token: () => google.maps.places.AutocompleteSessionToken | undefined;
  /** Forget the token: the next typing starts a new session. */
  end: () => void;
}

export const createPlacesSession = (): PlacesSession => {
  let current: google.maps.places.AutocompleteSessionToken | undefined;
  return {
    token: () => {
      const Token = window.google?.maps?.places?.AutocompleteSessionToken;
      if (!Token) return undefined;
      current ??= new Token();
      return current;
    },
    end: () => {
      current = undefined;
    },
  };
};

export const googlePlacesAvailable = (): boolean =>
  Boolean(
    window.google?.maps?.places?.AutocompleteService &&
    window.google?.maps?.places?.PlacesServiceStatus
  );

export const fetchPredictions = (
  input: string,
  session?: PlacesSession
): Promise<PlacePrediction[]> =>
  new Promise((resolve) => {
    if (!googlePlacesAvailable()) {
      resolve([]);
      return;
    }
    const places = window.google.maps.places;
    const _ = new places.AutocompleteService().getPlacePredictions(
      { input, sessionToken: session?.token() },
      (predictions, status) => {
        resolve(
          status === places.PlacesServiceStatus.OK && Array.isArray(predictions)
            ? predictions.map((p) => ({
                id: p.place_id ?? '',
                label: p.description ?? '',
                airport: p.types?.includes('airport') ?? false,
              }))
            : []
        );
      }
    );
  });

/**
 * The coordinates of a place. With a `session`, this is the place the user
 * picked: the details call carries the session's token and, once it answers,
 * ends the session (a failed call leaves it, so a retry stays in the same
 * session). A lookup without one is only a preview of the top match: it
 * carries no token and does not end the session the typing is part of.
 */
export const fetchPlaceCoordinates = (
  placeId: string,
  { session }: { session?: PlacesSession } = {}
): Promise<PlaceCoordinates> =>
  new Promise((resolve, reject) => {
    if (!googlePlacesAvailable()) {
      reject(new Error('Google Places is not available'));
      return;
    }
    const places = window.google.maps.places;
    new places.PlacesService(document.createElement('div')).getDetails(
      { placeId, fields: ['geometry'], sessionToken: session?.token() },
      (place, status) => {
        const location = place?.geometry?.location;
        if (status === places.PlacesServiceStatus.OK && location) {
          session?.end();
          resolve({ lat: location.lat(), lng: location.lng() });
        } else {
          reject(new Error(`Place lookup failed: ${status}`));
        }
      }
    );
  });
